/**
 * InsightsPage — life-area planetary influence analysis (ग्रह प्रभाव).
 *
 * Six life areas: Career / Relationships / Health / Finance / Travel / Education
 * Each shows:
 *  - 6-month rolling score trend (inline SVG line chart)
 *  - Current period summary and top driving layer
 *  - Upcoming classically favorable/cautionary key dates
 */
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  predictionsApi,
  gocharApi,
  isNotFoundError,
  type LifeAreaPrediction,
  type LifeArea,
  type PredictionScore,
  type PredictionKeyDate,
  type GocharTransit,
} from '@/lib/api'
import { useUser } from '@/store'
import { cn } from '@/lib/utils'
import { useFeatureGate } from '@/hooks/useSubscription'
import { HardGate } from '@/components/paywall'
import {
  TrendingUp, TrendingDown, Minus, RefreshCw,
  Briefcase, Heart, Activity, DollarSign, Plane, BookOpen,
  CalendarDays, ChevronUp, ChevronDown, Zap, ArrowRight,
} from 'lucide-react'
import dayjs from 'dayjs'
import { SkeletonCard, ErrorState, SetupPrompt } from '@/components/ui'

// ── Life area config ──────────────────────────────────────────────────────────

const AREA_CONFIG: Record<LifeArea, { label: string; sanskrit: string; icon: React.ReactNode }> = {
  career:        { label: 'Career',        sanskrit: 'करियर',       icon: <Briefcase   size={15} /> },
  relationships: { label: 'Relationships', sanskrit: 'रिश्ते',       icon: <Heart       size={15} /> },
  health:        { label: 'Health',        sanskrit: 'स्वास्थ्य',    icon: <Activity    size={15} /> },
  finance:       { label: 'Finance',       sanskrit: 'वित्त',        icon: <DollarSign  size={15} /> },
  travel:        { label: 'Travel',        sanskrit: 'यात्रा',       icon: <Plane       size={15} /> },
  education:     { label: 'Education',     sanskrit: 'शिक्षा',       icon: <BookOpen    size={15} /> },
}

const LIFE_AREAS: LifeArea[] = ['career', 'relationships', 'health', 'finance', 'travel', 'education']

// ── Score trend chart (SVG) ───────────────────────────────────────────────────

function ScoreChart({ data, color }: { data: PredictionScore[]; color: string }) {
  const W = 280
  const H = 80
  const PAD = { top: 8, right: 8, bottom: 20, left: 28 }

  const scores = data.map(d => d.score)
  const minS = Math.max(0,   Math.min(...scores) - 5)
  const maxS = Math.min(100, Math.max(...scores) + 5)

  const xStep = (W - PAD.left - PAD.right) / (data.length - 1)
  const yScale = (s: number) =>
    PAD.top + (H - PAD.top - PAD.bottom) * (1 - (s - minS) / (maxS - minS))

  const pts = data.map((d, i) => ({ x: PAD.left + i * xStep, y: yScale(d.score) }))
  const polyline = pts.map(p => `${p.x},${p.y}`).join(' ')
  const area = [
    `M${pts[0].x},${H - PAD.bottom}`,
    ...pts.map(p => `L${p.x},${p.y}`),
    `L${pts[pts.length - 1].x},${H - PAD.bottom}`,
    'Z',
  ].join(' ')

  const monthLabels = data.map(d => dayjs(d.date).format('MMM'))

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="w-full">
      {/* Grid lines */}
      {[25, 50, 75].map(v => {
        const y = yScale(v)
        return (
          <line key={v} x1={PAD.left} x2={W - PAD.right} y1={y} y2={y}
            style={{ stroke: 'var(--chart-border)' }} strokeWidth={1} />
        )
      })}
      {/* Area fill */}
      <path d={area} fill={color} fillOpacity={0.08} />
      {/* Line */}
      <polyline points={polyline} fill="none" stroke={color} strokeWidth={2}
        strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots */}
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill={color}
          style={{ filter: `drop-shadow(0 0 3px ${color}80)` }} />
      ))}
      {/* Month labels */}
      {monthLabels.map((m, i) => (
        <text key={i} x={pts[i].x} y={H - 4} textAnchor="middle"
          style={{ fill: 'var(--chart-text-dim)' }} fontSize={9} fontFamily="Geist Mono, monospace">
          {m}
        </text>
      ))}
      {/* Y axis label */}
      <text x={PAD.left - 4} y={yScale((minS + maxS) / 2)} textAnchor="end"
        style={{ fill: 'var(--chart-text-dim)' }} fontSize={9} dominantBaseline="middle"
        fontFamily="Geist Mono, monospace">
        {Math.round((minS + maxS) / 2)}
      </text>
    </svg>
  )
}

// ── Key date card ────────────────────────────────────────────────────────────

function KeyDateItem({ item }: { item: PredictionKeyDate }) {
  const isHigh = item.type === 'high'
  const daysOut = dayjs(item.date).diff(dayjs(), 'day')
  const label = daysOut === 0 ? 'Today' : daysOut === 1 ? 'Tomorrow' : `in ${daysOut}d`

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-cosmic-mist/50 last:border-0">
      <div className={cn(
        'mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center',
        isHigh ? 'bg-auspicious/15 text-auspicious' : 'bg-inauspicious/15 text-inauspicious',
      )}>
        {isHigh ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-2xs text-star leading-snug">{item.description}</p>
        <p className="mt-0.5 text-2xs font-mono text-star-dim">
          {dayjs(item.date).format('D MMM')} · <span className="text-gold-dim">{label}</span>
        </p>
      </div>
    </div>
  )
}

// ── Trend indicator ──────────────────────────────────────────────────────────

function TrendIcon({ trend }: { trend: LifeAreaPrediction['trend'] }) {
  if (trend === 'rising')  return <TrendingUp  size={14} className="text-auspicious" />
  if (trend === 'falling') return <TrendingDown size={14} className="text-inauspicious" />
  return <Minus size={14} className="text-star-dim" />
}

function scoreColor(score: number) {
  if (score >= 75) return '#22c55e'
  if (score >= 55) return '#f59e0b'
  if (score >= 35) return '#eab308'
  return '#ef4444'
}

// ── Planet symbol map ────────────────────────────────────────────────────────

const PLANET_SYMBOLS: Record<string, string> = {
  Sun:     '☉', Moon: '☽', Mars: '♂', Mercury: '☿',
  Jupiter: '♃', Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋',
}

const PLANET_COLORS: Record<string, string> = {
  Sun:     '#f97316', Moon:    '#c4b5fd', Mars:    '#ef4444',
  Mercury: '#22c55e', Jupiter: '#f59e0b', Venus:   '#ec4899',
  Saturn:  '#6366f1', Rahu:    '#8b5cf6', Ketu:    '#64748b',
}

// ── Transit card ──────────────────────────────────────────────────────────────

function TransitCard({ transit }: { transit: GocharTransit }) {
  const [expanded, setExpanded] = useState(false)
  const isPositive = transit.impact === 'positive'
  const isNegative = transit.impact === 'negative'
  const daysOut    = dayjs(transit.exactDate).diff(dayjs(), 'day')
  const timeLabel  = daysOut === 0 ? 'Today' : daysOut === 1 ? 'Tomorrow' : daysOut < 0 ? 'Active' : `in ${daysOut}d`
  const symbol     = PLANET_SYMBOLS[transit.planet] ?? '✦'
  const color      = PLANET_COLORS[transit.planet] ?? '#9090c0'

  return (
    <div className={cn(
      'card-cosmic p-3 border-l-2 transition-all duration-150',
      isPositive ? 'border-l-auspicious/60'
        : isNegative ? 'border-l-inauspicious/60'
        : 'border-l-star-dim/40',
    )}>
      <div className="flex items-start gap-3">
        {/* Planet badge */}
        <div
          className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-lg font-display"
          style={{ background: `${color}18`, border: `1px solid ${color}30`, color }}
        >
          {symbol}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-star-bright">{transit.planet}</span>
            <span className="text-2xs text-star-dim font-mono">in {transit.fromSign}</span>
            <span className={cn(
              'ml-auto text-2xs font-mono px-1.5 py-0.5 rounded-full border',
              isPositive ? 'text-auspicious bg-auspicious/10 border-auspicious/20'
                : isNegative ? 'text-inauspicious bg-inauspicious/10 border-inauspicious/20'
                : 'text-star-dim bg-star-dim/10 border-star-dim/20',
            )}>
              {timeLabel}
            </span>
          </div>
          <p className="text-2xs text-star-muted mt-0.5 capitalize">{transit.event}</p>
          <p className="text-2xs font-mono text-gold-dim mt-0.5">{dayjs(transit.exactDate).format('D MMM YYYY')}</p>
        </div>

        <button
          onClick={() => setExpanded(e => !e)}
          className="flex-shrink-0 p-1 text-star-dim hover:text-star-bright transition-colors"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-cosmic-mist/50">
          <p className="text-xs text-star-muted leading-relaxed">{transit.description}</p>
          <div className="mt-2 flex items-center gap-1.5">
            <Zap size={11} className={isPositive ? 'text-auspicious' : isNegative ? 'text-inauspicious' : 'text-star-dim'} />
            <span className="text-2xs text-star-dim capitalize">{transit.impact} influence</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Transits section ──────────────────────────────────────────────────────────

function TransitsSection({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['gochar-transits', userId],
    queryFn: () => gocharApi.getTransits(userId),
    retry: 1,
    staleTime: 10 * 60 * 1000,
  })

  if (isLoading) return (
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <SkeletonCard key={i} className="h-16" />
      ))}
    </div>
  )

  if (isError || !data || data.length === 0) return null

  const positives = data.filter(t => t.impact === 'positive')
  const negatives = data.filter(t => t.impact !== 'positive')

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ArrowRight size={12} className="text-gold-dim" />
        <p className="text-2xs font-mono uppercase tracking-wider text-star-dim">Active Transits</p>
        <span className="ml-auto text-2xs font-mono text-gold-dim">{data.length} planets</span>
      </div>

      {/* Positive transits first */}
      {positives.length > 0 && (
        <div className="space-y-2">
          {positives.map((t, i) => <TransitCard key={i} transit={t} />)}
        </div>
      )}

      {/* Challenging transits */}
      {negatives.length > 0 && (
        <div className="space-y-2">
          {negatives.map((t, i) => <TransitCard key={i} transit={t} />)}
        </div>
      )}
    </div>
  )
}

// ── Area panel ────────────────────────────────────────────────────────────────

function AreaPanel({ prediction }: { prediction: LifeAreaPrediction }) {
  const cfg = AREA_CONFIG[prediction.area]
  const color = scoreColor(prediction.currentScore)

  return (
    <div className="card-cosmic p-4 flex flex-col gap-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-gold-dim">{cfg.icon}</span>
          <div>
            <p className="text-sm font-medium text-star-bright">{cfg.label}</p>
            <p className="text-2xs font-display text-gold-dim/70">{cfg.sanskrit}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TrendIcon trend={prediction.trend} />
          <span className="text-xl font-bold font-mono" style={{ color }}>
            {prediction.currentScore}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-cosmic-void/60 rounded-lg p-2">
        <ScoreChart data={prediction.scoreTrend} color={color} />
      </div>

      {/* Top layer */}
      <div className="flex items-center gap-2">
        <span className={cn(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-medium border',
          prediction.topLayerImpact === 'positive'
            ? 'bg-auspicious/10 text-auspicious border-auspicious/20'
            : prediction.topLayerImpact === 'negative'
              ? 'bg-inauspicious/10 text-inauspicious border-inauspicious/20'
              : 'bg-star-dim/10 text-star-dim border-star-dim/20',
        )}>
          {prediction.topLayer}
        </span>
        <span className="text-2xs text-star-dim">driving this period</span>
      </div>

      {/* Summary */}
      <p className="text-xs text-star-muted leading-relaxed">{prediction.summary}</p>

      {/* Key dates */}
      {prediction.keyDates.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <CalendarDays size={12} className="text-gold-dim" />
            <span className="text-2xs font-mono uppercase tracking-wider text-star-dim">Key Dates</span>
          </div>
          <div>
            {prediction.keyDates.map((d, i) => (
              <KeyDateItem key={i} item={d} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function InsightsPage() {
  const gate = useFeatureGate('insights')
  const user = useUser()
  const [activeArea, setActiveArea] = useState<LifeArea>('career')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['insights'],
    queryFn: () => predictionsApi.getAll(),
    retry: 1,
  })

  const predictions = useMemo(
    () => data ?? [],
    [data],
  )

  const activePrediction = useMemo(
    () => predictions.find(p => p.area === activeArea) ?? predictions[0],
    [predictions, activeArea],
  )

  if (!gate.isLoading && !gate.allowed) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 p-4 sm:p-6">
        <div>
          <p className="text-2xs font-display text-gold-dim tracking-widest mb-1">ग्रह प्रभाव</p>
          <h1 className="text-xl font-bold text-star-bright">Planetary Influences</h1>
        </div>
        <div className="card-cosmic">
          <HardGate
            feature="Planetary Influences"
            unlockDescription="Life-area influence analysis is a Premium feature. Upgrade to see how planetary periods affect your career, relationships, health, and more."
          />
        </div>
      </div>
    )
  }

  if (isLoading) return (
    <div className="max-w-5xl mx-auto space-y-6 p-4 sm:p-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} className="h-16" />
        ))}
      </div>
      <SkeletonCard className="h-64" />
      <SkeletonCard className="h-48" />
    </div>
  )
  if (!data) {
    if (isNotFoundError(error)) return <SetupPrompt message="Set up your birth details to view planetary influence insights." />
    return <ErrorState message="Unable to load insights. Please try again." onRetry={refetch} />
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 p-4 sm:p-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-2xs font-display text-gold-dim tracking-widest mb-1">ग्रह प्रभाव</p>
          <h1 className="text-xl font-bold text-star-bright">Planetary Influences</h1>
          <p className="text-xs text-star-muted mt-0.5">Classical influence analysis across six life areas</p>
        </div>
        <button
          onClick={() => refetch()}
          className={cn(
            'p-2 rounded-lg border border-cosmic-mist text-star-dim hover:text-star-bright hover:border-violet-dark transition-colors',
            isLoading && 'animate-spin',
          )}
          aria-label="Refresh insights"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <HardGate locked={!gate.allowed} featureName="Planetary Influences">

      {/* Life area tab strip */}
      <div className="flex flex-wrap gap-2">
        {LIFE_AREAS.map(area => {
          const cfg = AREA_CONFIG[area]
          const pred = predictions.find(p => p.area === area)
          const score = pred?.currentScore
          const color = score !== undefined ? scoreColor(score) : '#4a4a8a'
          const isActive = area === activeArea
          return (
            <button
              key={area}
              onClick={() => setActiveArea(area)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                isActive
                  ? 'bg-cosmic-dusk border-violet-dark text-star-bright'
                  : 'bg-cosmic-void border-cosmic-mist text-star-dim hover:border-violet-dark hover:text-star-bright',
              )}
            >
              <span className={isActive ? 'text-gold' : 'text-star-dim'}>{cfg.icon}</span>
              {cfg.label}
              {score !== undefined && (
                <span className="font-mono text-2xs" style={{ color }}>{score}</span>
              )}
            </button>
          )
        })}
      </div>

      {/* Main area panel + overview grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active area — full detail */}
        <div className="lg:col-span-2">
          {activePrediction
            ? <AreaPanel prediction={activePrediction} />
            : !isLoading && <p className="text-star-dim text-sm p-4">No data available for this area.</p>
          }
        </div>

        {/* Overview scores + transits */}
        <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-2xs font-mono uppercase tracking-wider text-star-dim px-1">All Areas</p>
          {LIFE_AREAS.map(area => {
            const cfg = AREA_CONFIG[area]
            const pred = predictions.find(p => p.area === area)
            const score = pred?.currentScore ?? 0
            const color = scoreColor(score)
            const isActive = area === activeArea
            return (
              <button
                key={area}
                onClick={() => setActiveArea(area)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left',
                  isActive
                    ? 'bg-cosmic-dusk border-violet-dark'
                    : 'bg-cosmic-nebula border-cosmic-mist hover:border-violet-dark/50',
                )}
              >
                <span className="text-gold-dim flex-shrink-0">{cfg.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-star-bright">{cfg.label}</p>
                  {/* Score bar */}
                  <div className="mt-1 h-1 rounded-full bg-cosmic-void overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${score}%`, background: color }}
                    />
                  </div>
                </div>
                <span className="font-mono text-xs flex-shrink-0" style={{ color }}>{score}</span>
                {pred && <TrendIcon trend={pred.trend} />}
              </button>
            )
          })}
        </div>

        {/* Planetary transit cards */}
        {user?.id && (
          <div className="card-cosmic p-3">
            <TransitsSection userId={user.id} />
          </div>
        )}
        </div>
      </div>
      </HardGate>
    </div>
  )
}
